<script setup lang="ts">
import { computed } from "vue";
import type { DashboardBreakdownItem } from "../../features/dashboard/dashboard.types.ts";

const props = defineProps<{
  items: DashboardBreakdownItem[];
}>();

const total = computed(() =>
  props.items.reduce((sum, item) => sum + item.value, 0)
);

const donutBackground = computed(() => {
  if (total.value === 0) {
    return "conic-gradient(#1e293b 0deg 360deg)";
  }

  let currentPercent = 0;
  const segments: string[] = [];
  for (const item of props.items) {
    const nextPercent = currentPercent + (item.value / total.value) * 100;
    segments.push(
      `${item.color} ${String(currentPercent)}% ${String(nextPercent)}%`
    );
    currentPercent = nextPercent;
  }
  return `conic-gradient(${segments.join(", ")})`;
});
</script>

<template>
  <section class="dashboard-card rounded-[1.5rem] px-5 py-5">
    <div class="flex items-center justify-between gap-3">
      <h2 class="dashboard-text-primary text-lg font-semibold">
        Reason Breakdown
      </h2>
      <span class="dashboard-text-muted text-xs"
        >At-a-glance movement drivers</span
      >
    </div>

    <div class="mt-6 grid gap-6 xl:grid-cols-[16rem_1fr]">
      <div class="flex flex-col items-center justify-center">
        <div
          class="flex h-52 w-52 items-center justify-center rounded-full"
          :style="{ background: donutBackground }"
        >
          <div
            class="dashboard-donut-center flex h-32 w-32 flex-col items-center justify-center rounded-full"
          >
            <span class="dashboard-text-primary text-[2.1rem] font-semibold">{{
              total
            }}</span>
            <span class="dashboard-text-secondary text-center text-sm"
              >Total Movements</span
            >
          </div>
        </div>
      </div>

      <div class="grid gap-4">
        <div
          v-for="item in items"
          :key="item.label"
          class="dashboard-card--inner flex items-start justify-between gap-4 rounded-xl px-4 py-3"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-1 inline-flex h-3.5 w-3.5 rounded-full"
              :style="{ backgroundColor: item.color }"
            ></span>
            <span class="dashboard-text-primary text-sm">{{ item.label }}</span>
          </div>
          <span class="dashboard-text-primary text-sm font-semibold">
            {{ item.value }} ({{
              total > 0 ? Math.round((item.value / total) * 100) : 0
            }}%)
          </span>
        </div>

        <RouterLink
          :to="{ name: 'search-select-pos' }"
          class="dashboard-text-link mt-2 inline-flex items-center gap-2 text-sm font-semibold no-underline"
        >
          View reason details
          <span aria-hidden="true">›</span>
        </RouterLink>
      </div>
    </div>
  </section>
</template>
